import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { amountText, currencyName, parseAmount } from '../money';
import { Pick } from './Pick';

export interface AmountFieldProps {
  label: string;
  /** Minor units, or `null` while nothing usable is typed. */
  value: number | null;
  onChange: (minor: number | null) => void;
  currency: string;
  /** It must be filled in: its label is marked with a star. */
  required?: boolean;
  /** A problem to show under the field, already in words. */
  error?: string | undefined;
  /** Its label for a screen reader only, where the field's place says what it is. */
  quiet?: boolean;
  /** What it comes to when left empty, shown faint in it: `20.00`. */
  placeholder?: string | undefined;
  /** The moneys it can be in, picked at its end; without them, its one money is said there. */
  currencies?: readonly string[];
  onCurrency?: (currency: string) => void;
}

/**
 * An amount, in place of `<input type="number">` (docs/02 §6): plain text with the currency's
 * sign, `12.50`, `12,50` and `1 200` all understood, `null` reported while the text is not an
 * amount so a form cannot send a stale value.
 */
export function AmountField({
  label,
  value,
  onChange,
  currency,
  error,
  quiet = false,
  required = false,
  placeholder,
  currencies,
  onCurrency,
}: AmountFieldProps) {
  const { t, i18n } = useTranslation();
  const id = useId();
  const [text, setText] = useState(() => (value === null ? '' : amountText(value, currency)));
  const typed = parseAmount(text, currency);
  // Empty is "not filled in", which the form says; only text that is not an amount is wrong here.
  const message = typed === null && text.trim() !== '' ? t('field.amount') : error;

  return (
    <div className="field">
      <label
        className={quiet ? 'visually-hidden' : 'field-label'}
        htmlFor={id}
        data-required={required || undefined}
      >
        {label}
      </label>
      {/* The amount, then its money at its end, as one field: `[ 3 100 | JPY ▾ ]`. */}
      <div className="input-group">
        <input
          id={id}
          className="text-input amount-input"
          type="text"
          inputMode="decimal"
          autoComplete="off"
          placeholder={placeholder}
          value={text}
          aria-invalid={message !== undefined}
          aria-describedby={message === undefined ? undefined : `${id}-error`}
          onChange={(event) => {
            setText(event.target.value);
            onChange(parseAmount(event.target.value, currency));
          }}
          onBlur={() => {
            if (typed !== null) {
              setText(amountText(typed, currency));
            }
          }}
        />
        {currencies && onCurrency ? (
          <div className="input-suffix input-suffix-pick">
            <Pick
              label={t('field.currency', { field: label })}
              quiet
              suffix
              searchable={false}
              options={currencies.map((code) => ({
                value: code,
                label: `${code} · ${currencyName(code, i18n.language)}`,
                short: code,
              }))}
              value={currency}
              onChange={onCurrency}
            />
          </div>
        ) : (
          <span
            className="input-suffix"

            title={currencyName(currency, i18n.language)}
          >
            {currency}
          </span>
        )}
      </div>
      {message !== undefined && (
        <p className="field-error" id={`${id}-error`}>
          {message}
        </p>
      )}
    </div>
  );
}
